import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../assets/styles/auth.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);
  const token = searchParams.get('token');
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent double execution in React Strict Mode
      if (hasVerified.current) return;
      hasVerified.current = true;

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/verify-email/${token}`
        );

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          toast.success(response.data.message);
        }
      } catch (error) {
        setStatus('error');
        const errorMessage =
          error.response?.data?.message ||
          'Email verification failed. The link may be invalid or expired.';
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyEmail();
  }, [token]);

  const handleResendEmail = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResending(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/resend-verification`,
        { email }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setShowResendForm(false);
        setEmail('');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to resend verification email. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="verify-email-wrapper">
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-icon-container">
            {status === 'verifying' && (
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            {status === 'success' && (
              <div className="success-icon">
                <i className="bi bi-check-circle-fill text-success"></i>
              </div>
            )}
            {status === 'error' && (
              <div className="error-icon">
                <i className="bi bi-x-circle-fill text-danger"></i>
              </div>
            )}
          </div>

          <h2 className="verify-title">
            {status === 'verifying' && 'Verifying Your Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h2>

          <p className="verify-message">{message}</p>

          {status === 'error' && !showResendForm && (
            <div className="verify-actions">
              <button
                onClick={() => setShowResendForm(true)}
                className="btn btn-primary"
              >
                <i className="bi bi-envelope me-2"></i>
                Resend Verification Email
              </button>
              <Link to="/register" className="btn btn-outline-primary">
                <i className="bi bi-arrow-repeat me-2"></i>
                Back to Registration
              </Link>
            </div>
          )}

          {status === 'error' && showResendForm && (
            <form onSubmit={handleResendEmail} className="resend-form">
              <div className="form-group">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="verify-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isResending}
                >
                  {isResending ? 'Sending...' : 'Send Email'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowResendForm(false)}
                  className="btn btn-outline-primary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {status === 'success' && (
            <div className="verify-actions">
              <Link to="/login" className="btn btn-primary">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Go to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
